<template>
  <li>
    <a
      v-if="href"
      :rel="linkTarget === '_blank' ? 'noopener' : undefined"
      :target="linkTarget"
      :href="href"
      :class="$style.link"
      @click.exact="emitClick"
      @click.meta="handleClick"
      @click.ctrl="handleClick"
      @click.shift="handleClick"
      @auxclick="handleAuxClick"
      @contextmenu="handleContextMenu"
      @touchstart.passive="touchStartHandler"
      @touchend.passive="touchEndHandler"
      @touchmove.passive="touchMoveHandler"
      @touchcancel.passive="touchCancelHandler"
    >
      <div :class="$style.icon" aria-hidden="true">
        <slot name="icon"></slot>
      </div>
      <div :class="$style.text">
        <slot name="content"></slot>
      </div>
      <div>
        <HgIcon :class="$style.icon" icon="ChevronRight" size="large" theme="secondary" />
      </div>
    </a>
    <div v-else :class="$style.link" @click="emitClick">
      <div :class="$style.icon" aria-hidden="true">
        <slot name="icon"></slot>
      </div>
      <div :class="$style.text">
        <slot name="content"></slot>
      </div>
      <div>
        <HgIcon :class="$style.icon" icon="ChevronRight" size="large" theme="secondary" />
      </div>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { HgIcon } from '@smg-real-estate/domus';
import { computed } from 'vue';

import { type NewTabIntentEvent, useDetectNewTabIntent } from '@/composables/useDetectNewTabIntent';

defineOptions({
  name: 'PartnerListWidget',
});

interface Props {
  href?: string;
  openInNewTab?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  href: '',
  openInNewTab: true,
});

const emit = defineEmits<{
  click: [event: NewTabIntentEvent];
}>();

const emitClick = (event: NewTabIntentEvent): void => {
  emit('click', event);
};

const {
  handleClick,
  handleAuxClick,
  handleContextMenu,
  touchStartHandler,
  touchEndHandler,
  touchMoveHandler,
  touchCancelHandler,
} = useDetectNewTabIntent(emitClick);

const linkTarget = computed((): string => {
  return props.openInNewTab ? '_blank' : '_self';
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.link {
  @include body-2($color-grey-120);

  display: flex;
  flex-direction: row;
  align-items: center;
  padding: $space-50 $space-100;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid $color-grey-50;
  border-radius: $space-25;
  outline: none;

  &:hover {
    box-shadow: $elevation-minimal;
  }

  &:hover,
  &:active {
    text-decoration: none;
  }
}

.icon {
  display: flex;
}

.text {
  flex-grow: 1;
  margin-left: $space-100;
}
</style>
